'use client';

import { useMemo } from 'react';
import type { UserCasesSectionData } from '../../types';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell } from '../section-shared';

const COLUMN_OFFSETS = [48, 0, 80];

export function MasonryGrid({ section }: { section: UserCasesSectionData }) {
  const cases = section.cases ?? [];

  const columns = useMemo(() => {
    const cols: (typeof cases)[] = [[], [], []];
    cases.forEach((item, i) => {
      cols[i % 3].push(item);
    });
    return cols;
  }, [cases]);

  if (cases.length === 0) return null;

  return (
    <SectionShell>
      <div className="w-full text-center">
        <h2 className="font-semibold text-[36px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
        <PortableTextContent className="mt-3 text-base text-white/50 leading-[1.6]" value={section.description} />
      </div>

      <div className="mt-16 flex gap-5 sm:mt-8 sm:flex-col sm:gap-4">
        {columns?.map((col, colIdx) => (
          <div
            key={`col-${COLUMN_OFFSETS[colIdx]}`}
            className="flex flex-1 flex-col gap-5 sm:pt-0"
            style={{ paddingTop: COLUMN_OFFSETS[colIdx] }}
          >
            {col?.map((item) => (
              <a
                key={item._key}
                className="group/item relative block overflow-hidden rounded-[18px] border border-system-white08"
                href={item.workspaceLink}
                rel="noreferrer"
                target="_blank"
              >
                <MediaAsset className="aspect-[9/16] w-full object-cover" media={item.media} />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pt-10 pb-4 opacity-0 transition-opacity duration-300 group-hover/item:opacity-100">
                  <p className="font-medium text-base text-white leading-[1.2]">Prompt</p>
                  <p className="mt-2 text-sm text-white leading-[1.5]">{item.prompt}</p>
                </div>
              </a>
            ))}
          </div>
        ))}
      </div>
    </SectionShell>
  );
}
